import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import * as Lucide from "lucide-react";
import { SECTION_GROUPS, SECTION_META } from "./schemas";
import { Field } from "./fields";
import AdminAssistant from "./AdminAssistant";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ada_admin_token";

const formatErr = (detail) => {
  if (detail == null) return "Bir hata oluştu. Lütfen tekrar deneyin.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail?.msg || String(detail);
};

const Icon3 = ({ name, className = "h-4 w-4" }) => {
  const I = Lucide[name] || Lucide.Circle;
  return <I className={className} strokeWidth={1.5} />;
};

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, {
        email: email.trim().toLowerCase(),
        password: password.trim(),
      });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token, data.user);
      toast.success("Hoş geldiniz");
    } catch (err) {
      setError(formatErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-cream-100 px-6">
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 left-1/2 h-96 w-[42rem] -translate-x-1/2 rounded-full bg-gold/10 blur-[120px]" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md"
      >
        <div className="rounded-3xl border border-charcoal/5 bg-cream-50 p-10 shadow-lift">
          <div className="flex flex-col items-center">
            <img src="/assets/logo-mark-dark.png" alt="Ada" className="h-12 w-auto" />
            <p className="mt-4 font-accent text-[10px] uppercase tracking-[0.35em] text-gold-dark">
              Yönetim Paneli
            </p>
          </div>
          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="admin-login-form">
            <div>
              <span className="mb-2 block font-accent text-[11px] uppercase tracking-[0.2em] text-charcoal-900/50">
                E-posta
              </span>
              <input
                data-testid="admin-login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@samsunadayurdu.com"
                className="w-full rounded-xl border border-transparent bg-cream-100 px-4 py-3.5 text-sm outline-none transition-colors duration-300 focus:border-gold/40 focus:bg-cream-50 focus:ring-2 focus:ring-gold/20"
              />
            </div>
            <div>
              <span className="mb-2 block font-accent text-[11px] uppercase tracking-[0.2em] text-charcoal-900/50">
                Şifre
              </span>
              <input
                data-testid="admin-login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full rounded-xl border border-transparent bg-cream-100 px-4 py-3.5 text-sm outline-none transition-colors duration-300 focus:border-gold/40 focus:bg-cream-50 focus:ring-2 focus:ring-gold/20"
              />
            </div>
            {error && (
              <p data-testid="admin-login-error" className="rounded-xl bg-rose-100 px-4 py-3 text-xs font-medium text-rose-600">
                {error}
              </p>
            )}
            <button
              data-testid="admin-login-button"
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-charcoal-900 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-gold-dark hover:shadow-glow disabled:opacity-60"
            >
              {loading && <Lucide.Loader2 className="h-4 w-4 animate-spin" />}
              {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
            </button>
          </form>
        </div>
        <p className="mt-6 text-center text-xs text-charcoal-900/35">
          Bu alan yalnızca yetkili personel içindir.
        </p>
      </motion.div>
    </div>
  );
};

const LivePresence = ({ token }) => {
  const [data, setData] = useState({ active: 0, today: 0 });
  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    const load = () =>
      axios.get(`${API}/presence/active`, { headers }).then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [token]);
  return (
    <motion.div
      data-testid="live-presence"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="mb-6 flex flex-wrap items-center gap-x-8 gap-y-3 rounded-3xl border border-charcoal/5 bg-charcoal-900 p-6 text-cream-50 shadow-premium"
    >
      <div className="flex items-center gap-3">
        <span className="relative flex h-3 w-3">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-400" />
        </span>
        <span className="font-serif text-3xl" data-testid="live-active-count">{data.active}</span>
        <span className="text-sm text-cream-50/70">kişi şu an sitede</span>
      </div>
      <div className="h-8 w-px bg-cream-50/15" />
      <div className="flex items-baseline gap-2">
        <span className="font-serif text-2xl text-gold-light" data-testid="live-today-count">{data.today}</span>
        <span className="text-sm text-cream-50/70">son 24 saatte ziyaretçi</span>
      </div>
      <span className="ml-auto font-accent text-[10px] uppercase tracking-[0.2em] text-cream-50/40">Canlı · 15 sn'de bir güncellenir</span>
    </motion.div>
  );
};

const BarGroup = ({ title, data, testid }) => {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div data-testid={testid} className="rounded-3xl border border-charcoal/5 bg-cream-50 p-6 shadow-premium">
      <p className="font-serif text-lg text-charcoal-900">{title}</p>
      <div className="mt-6 flex h-40 items-end gap-1.5">
        {data.map((d, i) => (
          <div key={i} className="group flex flex-1 flex-col items-center justify-end gap-2">
            <span className="text-[10px] font-medium text-charcoal-900/0 transition-colors group-hover:text-charcoal-900/70">{d.count}</span>
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: `${(d.count / max) * 100}%` }}
              transition={{ duration: 0.6, delay: i * 0.02, ease: [0.22, 1, 0.36, 1] }}
              className="w-full min-h-[3px] rounded-t-md bg-gold-dark/80 transition-colors group-hover:bg-gold-dark"
              title={`${d.count}`}
            />
            <span className="text-[9px] tracking-tight text-charcoal-900/45">{d.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const VisitorChart = ({ token }) => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios
      .get(`${API}/presence/stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setStats(r.data))
      .catch(() => {});
  }, [token]);
  if (!stats) return null;
  return (
    <div data-testid="visitor-chart" className="mb-6 grid gap-5 lg:grid-cols-2">
      <BarGroup title="Son 24 Saat — Saatlik Ziyaret" data={stats.hourly} testid="chart-hourly" />
      <BarGroup title="Son 7 Gün — Günlük Ziyaretçi" data={stats.daily} testid="chart-daily" />
    </div>
  );
};

const Dashboard = ({ data, messages, appointments, sessions, token, onNavigate }) => {
  const cards = [
    { label: "Oda", value: data?.rooms?.length ?? 0, icon: "BedDouble", to: "rooms" },
    { label: "Hizmet", value: data?.services?.length ?? 0, icon: "ConciergeBell", to: "services" },
    { label: "Randevu", value: appointments.length, icon: "CalendarCheck", to: "appointments" },
    { label: "Gelen Mesaj", value: messages.length, icon: "Inbox", to: "messages" },
    { label: "Asistan Sohbeti", value: sessions.length, icon: "MessagesSquare", to: "assistantLogs" },
  ];
  return (
    <div data-testid="admin-dashboard">
      <LivePresence token={token} />
      <VisitorChart token={token} />
      <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-5">
        {cards.map((c, i) => (
          <motion.button
            key={c.label}
            data-testid={`dashboard-card-${c.to}`}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            onClick={() => onNavigate(c.to)}
            className="group rounded-3xl border border-charcoal/5 bg-cream-50 p-6 text-left shadow-premium transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gold/10 text-gold-dark transition-colors duration-300 group-hover:bg-gold/20">
              <Icon3 name={c.icon} className="h-5 w-5" />
            </div>
            <p className="mt-5 font-serif text-3xl text-charcoal-900">{c.value}</p>
            <p className="mt-1 text-xs text-charcoal-900/50">{c.label}</p>
          </motion.button>
        ))}
      </div>

      <div className="mt-10 rounded-3xl border border-charcoal/5 bg-cream-50 p-7 shadow-premium">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-xl text-charcoal-900">Son Mesajlar</h3>
          <button
            data-testid="dashboard-all-messages"
            onClick={() => onNavigate("messages")}
            className="text-xs font-semibold text-gold-dark transition-colors hover:text-gold"
          >
            Tümünü Gör
          </button>
        </div>
        <div className="mt-5 divide-y divide-charcoal/5">
          {messages.slice(0, 5).map((m, i) => (
            <div key={m.id || i} className="py-4">
              <div className="flex items-center justify-between gap-4">
                <p className="text-sm font-semibold text-charcoal-900">{m.name}</p>
                <p className="shrink-0 text-[11px] text-charcoal-900/40">
                  {new Date(m.created_at).toLocaleString("tr-TR")}
                </p>
              </div>
              <p className="mt-1 line-clamp-1 text-sm text-charcoal-900/55">{m.message}</p>
            </div>
          ))}
          {messages.length === 0 && (
            <p className="py-6 text-sm text-charcoal-900/45">Henüz mesaj yok.</p>
          )}
        </div>
      </div>
    </div>
  );
};

const Messages = ({ messages }) => (
  <div data-testid="admin-messages-list" className="space-y-4">
    {messages.map((m, i) => (
      <motion.div
        key={m.id || i}
        data-testid={`message-item-${i}`}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: i * 0.04, duration: 0.4 }}
        className="rounded-3xl border border-charcoal/5 bg-cream-50 p-6 shadow-premium"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-serif text-lg text-charcoal-900">{m.name}</p>
            <p className="mt-0.5 text-xs text-charcoal-900/50">
              {m.phone} • {m.email}
            </p>
          </div>
          <span className="rounded-full bg-gold/10 px-3.5 py-1.5 font-accent text-[10px] uppercase tracking-[0.15em] text-gold-dark">
            {new Date(m.created_at).toLocaleString("tr-TR")}
          </span>
        </div>
        <p className="mt-4 text-sm leading-relaxed text-charcoal-900/70">{m.message}</p>
      </motion.div>
    ))}
    {messages.length === 0 && (
      <p className="rounded-3xl border border-dashed border-charcoal/10 p-10 text-center text-sm text-charcoal-900/45">
        Henüz mesaj yok.
      </p>
    )}
  </div>
);

const Appointments = ({ appointments }) => (
  <div data-testid="admin-appointments-list" className="space-y-4">
    {appointments.map((a, i) => (
      <motion.div
        key={a.id || i}
        data-testid={`appointment-item-${i}`}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: i * 0.04, duration: 0.4 }}
        className="rounded-3xl border border-charcoal/5 bg-cream-50 p-6 shadow-premium"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gold/10 text-gold-dark">
              <Lucide.CalendarCheck className="h-5 w-5" strokeWidth={1.5} />
            </div>
            <div>
              <p className="font-serif text-lg text-charcoal-900">
                {a.name} {a.surname}
              </p>
              <a
                href={`tel:${a.phone}`}
                className="mt-0.5 block text-sm font-medium text-gold-dark transition-colors hover:text-gold"
              >
                {a.phone}
              </a>
            </div>
          </div>
          <span className="rounded-full bg-gold/10 px-3.5 py-1.5 font-accent text-[10px] uppercase tracking-[0.15em] text-gold-dark">
            {new Date(a.created_at).toLocaleString("tr-TR")}
          </span>
        </div>
        {(a.preferred_date || a.preferred_time) && (
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-charcoal-900/5 px-3.5 py-2 text-xs font-medium text-charcoal-900/70">
            <Lucide.CalendarClock className="h-4 w-4 text-gold-dark" strokeWidth={1.5} />
            Tercih edilen: {a.preferred_date ? new Date(`${a.preferred_date}T00:00:00`).toLocaleDateString("tr-TR", { day: "numeric", month: "long", weekday: "long" }) : ""}
            {a.preferred_time ? ` · ${a.preferred_time}` : ""}
          </div>
        )}
        {a.note && <p className="mt-4 text-sm leading-relaxed text-charcoal-900/70">{a.note}</p>}
      </motion.div>
    ))}
    {appointments.length === 0 && (
      <p className="rounded-3xl border border-dashed border-charcoal/10 p-10 text-center text-sm text-charcoal-900/45">
        Henüz randevu talebi yok.
      </p>
    )}
  </div>
);

const AssistantLogs = ({ sessions }) => {
  const [openId, setOpenId] = useState(null);
  return (
    <div data-testid="admin-assistant-logs" className="space-y-3">
      {sessions.map((s, i) => {
        const firstUser = s.messages.find((m) => m.role === "user");
        const preview = firstUser ? firstUser.content : "Konuşma";
        const isOpen = openId === s.session_id;
        return (
          <div
            key={s.session_id}
            data-testid={`session-item-${i}`}
            className="overflow-hidden rounded-3xl border border-charcoal/5 bg-cream-50 shadow-premium"
          >
            <button
              data-testid={`session-toggle-${i}`}
              onClick={() => setOpenId(isOpen ? null : s.session_id)}
              className="flex w-full items-center justify-between gap-4 p-5 text-left transition-colors hover:bg-cream-100"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-charcoal-900">{preview}</p>
                <p className="mt-0.5 text-xs text-charcoal-900/45">
                  {s.count} mesaj • {new Date(s.last).toLocaleString("tr-TR")}
                </p>
              </div>
              <Lucide.ChevronDown
                className={`h-4 w-4 shrink-0 text-charcoal-900/40 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                strokeWidth={1.5}
              />
            </button>
            {isOpen && (
              <div className="space-y-3 border-t border-charcoal/5 bg-cream-100/50 p-5">
                {s.messages.map((m, j) => (
                  <div key={j} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[80%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                        m.role === "user"
                          ? "rounded-br-md bg-charcoal-900 text-cream-50"
                          : "rounded-bl-md border border-charcoal/5 bg-cream-50 text-charcoal-900"
                      }`}
                    >
                      {m.content.replace(/\[WHATSAPP\]/g, "").replace(/\[RANDEVU\]/g, "").trim()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
      {sessions.length === 0 && (
        <p className="rounded-3xl border border-dashed border-charcoal/10 p-10 text-center text-sm text-charcoal-900/45">
          Henüz asistan konuşması yok.
        </p>
      )}
    </div>
  );
};

const Panel = ({ token, user, onLogout }) => {
  const [data, setData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState("dashboard");
  const [saving, setSaving] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/content`).then((r) => setData(r.data)).catch(() => toast.error("İçerik yüklenemedi"));
    axios
      .get(`${API}/contact`, { headers })
      .then((r) => setMessages(r.data))
      .catch(() => {});
    axios
      .get(`${API}/appointment`, { headers })
      .then((r) => setAppointments(r.data))
      .catch(() => {});
    axios
      .get(`${API}/chat/sessions`, { headers })
      .then((r) => setSessions(r.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const meta = SECTION_META[active];
  const isEditable = meta?.fields && data;

  const update = (key, v) => setData((d) => ({ ...d, [key]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content/${active}`, { value: data[active] }, { headers });
      toast.success(`"${meta.label}" kaydedildi`);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const SidebarContent = () => (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 px-6 pb-8 pt-7">
        <img src="/assets/logo-mark-dark.png" alt="Ada" className="h-9 w-auto" />
      </div>
      <nav className="flex-1 space-y-6 overflow-y-auto px-4 pb-6" data-lenis-prevent>
        {SECTION_GROUPS.map((g) => (
          <div key={g.label}>
            <p className="mb-2 px-3 font-accent text-[10px] uppercase tracking-[0.25em] text-charcoal-900/35">
              {g.label}
            </p>
            <div className="space-y-1">
              {g.items.map((key) => {
                const m = SECTION_META[key];
                const isActive = active === key;
                return (
                  <button
                    key={key}
                    data-testid={`admin-nav-${key}`}
                    onClick={() => {
                      setActive(key);
                      setMenuOpen(false);
                    }}
                    className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors duration-300 ${
                      isActive
                        ? "bg-gold/10 font-medium text-gold-dark"
                        : "text-charcoal-900/60 hover:bg-rose-50 hover:text-charcoal-900"
                    }`}
                  >
                    <Icon3 name={m.icon} className="h-4 w-4" />
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
      <div className="space-y-2 border-t border-charcoal/5 p-4">
        <a
          data-testid="admin-view-site"
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-charcoal-900/60 transition-colors duration-300 hover:bg-rose-50 hover:text-charcoal-900"
        >
          <Lucide.ExternalLink className="h-4 w-4" strokeWidth={1.5} />
          Siteyi Görüntüle
        </a>
        <button
          data-testid="admin-logout-button"
          onClick={onLogout}
          className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-rose-600 transition-colors duration-300 hover:bg-rose-100"
        >
          <Lucide.LogOut className="h-4 w-4" strokeWidth={1.5} />
          Çıkış Yap
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-cream-100 md:grid md:grid-cols-[260px_1fr]">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-[260px] border-r border-charcoal/5 bg-cream-50 md:block">
        <SidebarContent />
      </aside>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-charcoal-950/40 backdrop-blur-sm md:hidden"
            onClick={() => setMenuOpen(false)}
          >
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="h-full w-[260px] bg-cream-50 shadow-lift"
              onClick={(e) => e.stopPropagation()}
            >
              <SidebarContent />
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="md:col-start-2">
        <header className="sticky top-0 z-30 border-b border-charcoal/5 bg-cream-100/85 backdrop-blur-xl">
          <div className="flex items-center justify-between gap-4 px-5 py-4 md:px-10">
            <div className="flex items-center gap-3">
              <button
                data-testid="admin-mobile-menu"
                onClick={() => setMenuOpen(true)}
                aria-label="Menü"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-charcoal/10 bg-cream-50 md:hidden"
              >
                <Lucide.Menu className="h-4 w-4" strokeWidth={1.5} />
              </button>
              <div>
                <h1 className="font-serif text-xl tracking-tight text-charcoal-900 md:text-2xl">
                  {meta?.label || "Yönetim Paneli"}
                </h1>
                {meta?.desc && <p className="hidden text-xs text-charcoal-900/45 md:block">{meta.desc}</p>}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="hidden text-xs text-charcoal-900/40 lg:block">{user?.email}</span>
              {isEditable && (
                <button
                  data-testid="admin-save-button"
                  onClick={save}
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:bg-gold-dark hover:shadow-glow disabled:opacity-60"
                >
                  {saving && <Lucide.Loader2 className="h-4 w-4 animate-spin" />}
                  {saving ? "Kaydediliyor..." : "Kaydet"}
                </button>
              )}
            </div>
          </div>
        </header>

        <main className="px-5 py-8 md:px-10 md:py-12">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-4xl"
          >
            {!data ? (
              <div className="flex items-center gap-3 text-sm text-charcoal-900/50">
                <Lucide.Loader2 className="h-4 w-4 animate-spin" /> İçerik yükleniyor...
              </div>
            ) : active === "dashboard" ? (
              <Dashboard data={data} messages={messages} appointments={appointments} sessions={sessions} token={token} onNavigate={setActive} />
            ) : active === "assistant" ? (
              <AdminAssistant token={token} />
            ) : active === "messages" ? (
              <Messages messages={messages} />
            ) : active === "appointments" ? (
              <Appointments appointments={appointments} />
            ) : active === "assistantLogs" ? (
              <AssistantLogs sessions={sessions} />
            ) : (
              <div className="space-y-6">
                {meta.fields.map((f) => (
                  <div
                    key={f.key || "root"}
                    className={f.type === "group" || f.type === "array" ? "" : "rounded-2xl"}
                  >
                    <div className={f.type === "group" || f.type === "array" ? "mb-3" : ""}>
                      <p className="mb-2 font-accent text-[11px] uppercase tracking-[0.2em] text-charcoal-900/50">
                        {f.label}
                      </p>
                    </div>
                    <Field
                      spec={f}
                      token={token}
                      value={f.root ? data[active] : data[active]?.[f.key]}
                      onChange={(v) =>
                        f.root ? update(active, v) : update(active, { ...data[active], [f.key]: v })
                      }
                    />
                  </div>
                ))}
                <p className="pt-2 text-xs text-charcoal-900/35">
                  Değişiklikler "Kaydet" butonuna basınca yayınlanır. Başlıklarda *yıldız* arasındaki
                  kelimeler altın italik vurgu alır.
                </p>
              </div>
            )}
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default function AdminApp() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(!!localStorage.getItem(TOKEN_KEY));

  useEffect(() => {
    document.title = "Yönetim Paneli | Ada";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  useEffect(() => {
    if (!token) {
      setChecking(false);
      return;
    }
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setUser(r.data))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      })
      .finally(() => setChecking(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {}
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
    toast.success("Çıkış yapıldı");
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream-100">
        <Lucide.Loader2 className="h-6 w-6 animate-spin text-gold-dark" />
      </div>
    );
  }

  if (!token || !user) {
    return (
      <Login
        onLogin={(t, u) => {
          setToken(t);
          setUser(u);
        }}
      />
    );
  }

  return <Panel token={token} user={user} onLogout={logout} />;
}
